import { useRef, useState } from "react";
import { MainService } from "@/shared/services/main.service";
import { generateCalculationCode } from "@/features/finance/kapital/services/kapital.utils";
import type { Calculation } from "@/shared/types";
import type {
  FinancialTable,
  FormData,
  ValoraSensibilidadEntry,
} from "@/shared/types/ValoraTypes";
import type { ToastType } from "@/shared/types/toast.types";
import type { NativeValoraResponse } from "@/shared/services/valora-native.service";

export type ValoraResultView = "original" | "sensibilidad" | "comparacion";

const normalizeTableThousandsSeparators = (
  table: FinancialTable | null
): FinancialTable | null => {
  if (!table) return table;

  return {
    ...table,
    rows: table.rows.map((row) => ({
      ...row,
      values: row.values.map((rawValue) => {
        const value = Number(rawValue);
        return Number.isFinite(value) &&
          !Number.isInteger(value) &&
          Math.abs(value) < 10_000
          ? Math.round(value * 1_000)
          : value;
      }),
    })),
  };
};

interface UseValoraCalculationProps {
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  balanceTable: FinancialTable | null;
  setBalanceTable: React.Dispatch<React.SetStateAction<FinancialTable | null>>;
  resultsTable: FinancialTable | null;
  setResultsTable: React.Dispatch<React.SetStateAction<FinancialTable | null>>;
  fileUploaded: boolean;
  setFileUploaded?: React.Dispatch<React.SetStateAction<boolean>>;
  addToast: (type: ToastType, message: string) => void;
  userId?: number | string | null;
  ui: {
    setShowResults: (show: boolean) => void;
    setIsDesktopFormOpen: (open: boolean) => void;
    setResultsSection: (section: "resultados" | "estados") => void;
  };
}

export function useValoraCalculation({
  formData,
  setFormData,
  balanceTable,
  setBalanceTable,
  resultsTable,
  setResultsTable,
  fileUploaded,
  setFileUploaded,
  addToast,
  userId,
  ui,
}: UseValoraCalculationProps) {
  const [currentCalculation, setCurrentCalculation] = useState<Calculation | null>(null);
  const [isLoading] = useState(false);
  const [isNativeLoading, setIsNativeLoading] = useState(false);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [resultView, setResultView] = useState<ValoraResultView>("original");
  const [sensibilizaciones, setSensibilizaciones] = useState<ValoraSensibilidadEntry[]>([]);
  const [selectedSensIdx, setSelectedSensIdx] = useState(0);
  const [isSessionFresh, setIsSessionFresh] = useState(false);
  const loadFromUrlCalledRef = useRef(false);

  const parseWaccValue = (raw: unknown): number | undefined => {
    if (raw === undefined || raw === null || raw === "") return undefined;
    if (typeof raw === "number") return Number.isFinite(raw) ? raw : undefined;
    const str = String(raw).trim().replace("%", "").replace(",", ".");
    const num = Number(str);
    if (!Number.isFinite(num)) return undefined;
    return Math.abs(num) < 1 && num !== 0 ? num * 100 : num;
  };

  const extractSensibilizaciones = (
    data: unknown
  ): ValoraSensibilidadEntry[] => {
    if (!data || typeof data !== "object") return [];
    const d = data as Record<string, unknown>;
    const raw = d.sensibilizacion ?? d.sensibilidad;
    if (!Array.isArray(raw)) return [];
    return raw.map((entry: any) => {
      const inputs = entry.inputs ?? entry.input ?? {};
      const resultados = entry.resultados ?? entry.resultado ?? {};
      const waccRaw =
        entry.wacc ??
        entry.WACC ??
        resultados.wacc ??
        resultados.WACC ??
        entry.wacc_emergente ??
        inputs.wacc ??
        inputs.WACC;
      const parseNum = (v: unknown) => {
        if (v === undefined || v === null || String(v).trim() === "") return undefined;
        const n = Number(String(v).replace(",", "."));
        return Number.isFinite(n) ? n : undefined;
      };
      return {
        ...entry,
        // Histórico sin subsector queda undefined -> selector mostrará "Escenario N" + Beta en subtítulo
        subsector:
          entry.subsector ??
          entry.sector ??
          inputs.subsector ??
          inputs.subsector_sensibilizacion ??
          inputs.sector ??
          undefined,
        wacc: parseWaccValue(waccRaw),
        revenue_forecast_rate: parseNum(
          entry.revenue_forecast_rate ??
            entry.forecast_ingresos ??
            inputs.revenue_forecast_rate ??
            inputs.forecast_ingresos ??
            inputs.revenue_forecast_rate ??
            entry.tasa_forecast
        ),
        fdc_forecast_rate: parseNum(
          entry.fdc_forecast_rate ??
            entry.forecast_fde ??
            inputs.fdc_forecast_rate ??
            inputs.forecast_fde ??
            entry.tasa_fdc
        ),
        perpetual_growth_rate: parseNum(
          entry.perpetual_growth_rate ??
            entry.crecimiento_perpetuo ??
            inputs.perpetual_growth_rate ??
            inputs.crecimiento_perpetuo ??
            entry.tasa_perpetua
        ),
      } as ValoraSensibilidadEntry;
    });
  };

  const getCodeFromUrl = (): string | null => {
    const pathname = window.location.pathname;
    const parts = pathname.split("/").filter(Boolean);
    if (parts.length >= 2 && parts[0] === "valora") {
      return parts[1];
    }
    const params = new URLSearchParams(window.location.search);
    return params.get("calc") || params.get("code");
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    return handleNativeSubmit(e);
    /* Legacy API calculation path retained temporarily for rollback.
    if (e) e.preventDefault();

    const missingFields: string[] = [];
    if (!formData.date) missingFields.push("Fecha");
    if (!formData.country) missingFields.push("País");
    if (!formData.currency) missingFields.push("Moneda");
    if (!formData.sector) missingFields.push("Sector");
    if (!fileUploaded && !hasCalculated && !formData.fileUsername) {
      missingFields.push("Plantilla EEFF");
    }

    if (missingFields.length > 0) {
      addToast("warn", `Completa los campos: ${missingFields.join(", ")}`);
      return;
    }

    let currentUserId = userId;
    if (!currentUserId) {
      try {
        const storedUser = localStorage.getItem("user_data");
        if (storedUser) {
          const parsed = JSON.parse(storedUser);
          currentUserId = parsed.id;
        }
      } catch (err) {
        console.warn("Could not read user from storage", err);
      }
    }

    ui.setShowResults(false);
    setIsLoading(true);

    const normalizedBalanceTable =
      normalizeTableThousandsSeparators(balanceTable);
    const normalizedResultsTable =
      normalizeTableThousandsSeparators(resultsTable);

    if (normalizedBalanceTable !== balanceTable) {
      console.info(
        "[VALORA][UPLOAD] Separadores de miles normalizados en el balance."
      );
    }

    if (normalizedResultsTable !== resultsTable) {
      console.info(
        "[VALORA][UPLOAD] Separadores de miles normalizados en resultados."
      );
    }

    const inputPayload = {
      ...formData,
      balance_table: normalizedBalanceTable,
      results_table: normalizedResultsTable,
    };
    // formData Tasas debug removed

    try {
      let persistedCalculation: Calculation;
      if (currentCalculation) {
        persistedCalculation = await MainService.updateNativeCalculation(
          currentCalculation.id,
          {
            data: {
              inputs: [inputPayload],
            },
          }
        );
      } else {
        persistedCalculation = await MainService.createNativeCalculation({
          calculation_file_id: null,
          user_id: currentUserId ? Number(currentUserId) : null,
          code: generateCalculationCode(),
          type: "valora",
          data: {
            inputs: [inputPayload],
          },
        });

        window.history.pushState(
          {},
          "",
          `/valora/${persistedCalculation.code}`
        );
      }

      // Parche frontend: backend no persiste subsector nombre, inyecta último seleccionado localmente
      const rawSens = extractSensibilizaciones(persistedCalculation.data);
      let patchedSens = rawSens;
      const lastSelectedSubsector = (formData as any).subsector_sensibilizacion || (formData as any).subsector;
      if (lastSelectedSubsector && rawSens.length > 0 && !rawSens[0]?.subsector) {
        patchedSens = rawSens.map((entry, idx) =>
          idx === 0 ? { ...entry, subsector: lastSelectedSubsector } : entry
        );
        // Persiste parche en data para que recarga mantenga nombre sin depender de backend
        (persistedCalculation.data as any).sensibilizacion = patchedSens;
        // Intenta guardar en backend de forma best-effort (no bloquea)
        if (currentCalculation) {
          MainService.updateNativeCalculation(persistedCalculation.id, {
            data: { sensibilizacion: patchedSens } as any,
          }).catch(() => {});
        }
      }

      setCurrentCalculation(persistedCalculation);
      setHasCalculated(true);
      setIsSessionFresh(true);

      const sensibilizacionesData = patchedSens;
      setSensibilizaciones(sensibilizacionesData);
      setSelectedSensIdx(
        sensibilizacionesData.length > 0 ? 0 : 0
      );

      const hasSensitivity = sensibilizacionesData.length > 0;
      setResultView(hasSensitivity ? "sensibilidad" : "original");

      ui.setShowResults(true);
      ui.setIsDesktopFormOpen(false);
      ui.setResultsSection("resultados");

      addToast("success", "Cálculo Valora generado y guardado correctamente.");
    } catch (error) {
      console.error("Error in Valora calculation", error);
      addToast("error", "No se pudo guardar el cálculo. Intenta nuevamente.");
    } finally {
      setIsLoading(false);
    }
  };

    */
  };

  const handleNativeSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    const missingFields: string[] = [];
    if (!formData.date) missingFields.push("Fecha");
    if (!formData.country) missingFields.push("País");
    if (!formData.currency) missingFields.push("Moneda");
    if (!formData.sector) missingFields.push("Sector");
    if (!fileUploaded && !hasCalculated && !formData.fileUsername) {
      missingFields.push("Plantilla EEFF");
    }

    if (missingFields.length > 0) {
      addToast("warn", `Completa los campos: ${missingFields.join(", ")}`);
      return;
    }

    let currentUserId = userId;
    if (!currentUserId) {
      try {
        const storedUser = localStorage.getItem("user_data");
        if (storedUser) {
          const parsed = JSON.parse(storedUser);
          currentUserId = parsed.id;
        }
      } catch (err) {
        console.warn("Could not read user from storage", err);
      }
    }

    ui.setShowResults(false);
    setIsNativeLoading(true);

    const normalizedBalanceTable =
      normalizeTableThousandsSeparators(balanceTable);
    const normalizedResultsTable =
      normalizeTableThousandsSeparators(resultsTable);

    const inputPayload = {
      ...formData,
      balance_table: normalizedBalanceTable,
      results_table: normalizedResultsTable,
    };
    const calculationCode = getCodeFromUrl() ?? generateCalculationCode();

    const numOrUndefined = (v: unknown): number | undefined => {
      if (v === undefined || v === null || String(v).trim() === "") return undefined;
      const n = Number(String(v).replace(",", "."));
      return Number.isFinite(n) ? n : undefined;
    };

    let sensitivity: Record<string, unknown> | null = null;
    if (hasCalculated) {
      const sensBeta =
        numOrUndefined((formData as any).beta_subsector) ??
        numOrUndefined((formData as any).beta_unlevered_sensitivity) ??
        numOrUndefined((formData as any).beta_subsector_custom) ??
        numOrUndefined((formData as any).beta_desapalancado) ??
        numOrUndefined((formData as any).beta_unlevered) ??
        numOrUndefined((formData as any).beta_unlevered_industry);
      const sensRates = {
        revenue_forecast_rate: numOrUndefined((formData as any).revenue_forecast_rate),
        fdc_forecast_rate: numOrUndefined((formData as any).fdc_forecast_rate),
        perpetual_growth_rate: numOrUndefined((formData as any).perpetual_growth_rate),
        capex_income_rate: numOrUndefined((formData as any).capex_income_rate),
        cto_income_rate: numOrUndefined((formData as any).cto_income_rate),
      };
      const hasRates = Object.values(sensRates).some((v) => v !== undefined);
      if (sensBeta !== undefined) {
        // B20/Custom: normalizar a "" cuando no hay subsector elegido (beta manual).
        // Así el backend escribe "Custom" en WACC!B20 en vez de omitir la key.
        const sensSubsector = (formData as any).subsector_sensibilizacion ?? "";
        sensitivity = {
          beta_desapalancado: sensBeta,
          beta_subsector: (formData as any).beta_subsector,
          subsector: sensSubsector,
          subsector_sensibilizacion: sensSubsector,
          tickers: (formData as any).tickers_subsector_sensibilizacion ?? "",
          industria: (formData as any).sector,
        };
        if (hasRates) {
          if (sensRates.revenue_forecast_rate !== undefined)
            (sensitivity as Record<string, unknown>).revenue_forecast_rate = sensRates.revenue_forecast_rate;
          if (sensRates.fdc_forecast_rate !== undefined)
            (sensitivity as Record<string, unknown>).fdc_forecast_rate = sensRates.fdc_forecast_rate;
          if (sensRates.perpetual_growth_rate !== undefined)
            (sensitivity as Record<string, unknown>).perpetual_growth_rate = sensRates.perpetual_growth_rate;
          if (sensRates.capex_income_rate !== undefined)
            (sensitivity as Record<string, unknown>).capex_income_rate = sensRates.capex_income_rate;
          if (sensRates.cto_income_rate !== undefined)
            (sensitivity as Record<string, unknown>).cto_income_rate = sensRates.cto_income_rate;
        }
      }
    }

    try {
      // El proxy resuelve la plantilla predeterminada desde la BD, igual que
      // Kapital, y la reenvía al web-service junto con el cálculo.
      const res = (await MainService.calculateValoraExcel(
        inputPayload as unknown as Record<string, unknown>,
        sensitivity,
        userId,
        calculationCode
      )) as unknown as NativeValoraResponse;

      if (!res.success) {
        throw new Error("El servicio nativo no devolvió éxito");
      }

      const now = new Date().toISOString();
      const formatRateInput = (value: unknown): string => {
        if (value == null || value === "") return "";
        const numeric = Number(String(value).replace("%", "").replace(",", "."));
        if (!Number.isFinite(numeric)) return "";
        const percentage = Math.abs(numeric) < 1 && numeric !== 0
          ? numeric * 100
          : numeric;
        return String(Math.round(percentage * 100) / 100);
      };
      // Igual que Kapital (useKapitalCalculation + COUNTRY_LOCAL_CURRENCIES):
      // la moneda origen debe persistirse para que el select de resultados
      // muestre PEN (u otra moneda local) en vez de caer al fallback "USD".
      const nativeSourceCurrency = (res as any)?.source_currency as string | undefined;
      const nativeFxToUsd = (res as any)?.fx_to_usd as number | undefined;
      const inputCurrency = String(
        (inputPayload as any)?.currency ?? (inputPayload as any)?.moneda ?? ""
      ).toUpperCase();
      const sourceCurrency = String(
        nativeSourceCurrency ?? inputCurrency ?? "USD"
      ).toUpperCase() || "USD";
      const baseResultados = {
        created_at: now,
        wacc: res.wacc ?? (res.conceptos as any)?.wacc ?? null,
        wacc_emergente: res.wacc_emergente ?? null,
        capex_income_rate: res.capex_income_rate ?? null,
        cto_income_rate: res.cto_income_rate ?? null,
        source_currency: sourceCurrency,
        fx_to_usd: nativeFxToUsd ?? (sourceCurrency === "USD" ? 1 : null),
        inputs: { moneda: sourceCurrency },
        balance: res.balance ?? {},
        conceptos: res.conceptos ?? {},
        integrado: res.integrado ?? {},
        conceptos_emergente: res.conceptos_emergente ?? {},
        integrado_emergente: res.integrado_emergente ?? {},
      };
      const sensEntries = (res.sensitivity_results ?? []).map((entry) => ({
        ...entry,
        created_at: now,
        source_currency: (entry as any)?.source_currency ?? sourceCurrency,
        fx_to_usd: (entry as any)?.fx_to_usd ?? baseResultados.fx_to_usd,
      }));
      const previousData = (currentCalculation?.data || {}) as Record<string, unknown>;
      const previousSens = Array.isArray(previousData.sensibilizacion)
        ? previousData.sensibilizacion
        : Array.isArray(previousData.sensibilidad)
          ? previousData.sensibilidad
          : [];
      const allSensEntries = [...previousSens, ...sensEntries];

      const persistedData = {
        inputs: [{ ...inputPayload, created_at: now }],
        resultados: [baseResultados],
        sensibilizacion: allSensEntries,
      };
      const persistedCalculation =
        currentCalculation && currentCalculation.id > 0
          ? await MainService.updateNativeCalculation(currentCalculation.id, {
              data: persistedData,
            })
          : await MainService.createNativeCalculation({
              calculation_file_id: null,
              user_id: currentUserId ? Number(currentUserId) : null,
          code: calculationCode,
              type: "valora",
              data: persistedData,
            });

      if (!currentCalculation || currentCalculation.id === 0) {
        window.history.pushState({}, "", `/valora/${persistedCalculation.code}`);
      }

      const nativeCalculation = {
        code: persistedCalculation.code,
        id: persistedCalculation.id,
        calculation_file_id: (res.s3_key as string) ?? null,
        user_id: currentUserId ? Number(currentUserId) : 0,
        type: "valora",
        data: persistedData,
        created_at: now,
        updated_at: now,
      } as unknown as Calculation;

      setCurrentCalculation(nativeCalculation);
      setFormData((prev) => ({
        ...prev,
        ...(prev.revenue_forecast_rate ? {} : {
          revenue_forecast_rate: formatRateInput((res.conceptos as any)?.tasa_forecast),
        }),
        ...(prev.fdc_forecast_rate ? {} : {
          fdc_forecast_rate: formatRateInput((res.integrado as any)?.tasa_forecast),
        }),
        ...(prev.perpetual_growth_rate ? {} : {
          perpetual_growth_rate: formatRateInput(
            (res.conceptos as any)?.tasa_perpetua ?? (res.integrado as any)?.tasa_perpetua,
          ),
        }),
        ...(prev.capex_income_rate ? {} : {
          capex_income_rate: formatRateInput(baseResultados.capex_income_rate),
        }),
        ...(prev.cto_income_rate ? {} : {
          cto_income_rate: formatRateInput(baseResultados.cto_income_rate),
        }),
      }));
      setHasCalculated(true);
      setIsSessionFresh(true);

      const sensibilizacionesData = extractSensibilizaciones(nativeCalculation.data);
      setSensibilizaciones(sensibilizacionesData);
      setSelectedSensIdx(Math.max(0, sensibilizacionesData.length - 1));

      const hasSensitivity = sensibilizacionesData.length > 0;
      setResultView(hasSensitivity ? "sensibilidad" : "original");

      ui.setShowResults(true);
      ui.setIsDesktopFormOpen(false);
      ui.setResultsSection("resultados");

      addToast(
        "success",
        res.s3_key
          ? `Cálculo nativo listo. Copia debug en S3: ${res.s3_key}`
          : "Cálculo nativo listo (sin espejo S3, revisa el servicio)."
      );
    } catch (error) {
      console.error("Error in native Valora calculation", error);
      addToast(
        "error",
        error instanceof Error ? error.message : "No se pudo calcular con Excel nativo."
      );
    } finally {
      setIsNativeLoading(false);
    }
  };

  const loadFromUrl = async () => {    if (loadFromUrlCalledRef.current) return;
    loadFromUrlCalledRef.current = true;
    try {
      const code = getCodeFromUrl();
      if (!code) return;

      const calculationData = await MainService.getCalculationByCode(code);
      if (calculationData && calculationData.type === "valora") {
        setCurrentCalculation(calculationData);

        const dataObj = calculationData.data as { inputs?: any[] };
        const inputs = dataObj?.inputs || [];
        const latestInput = inputs[inputs.length - 1];

        if (latestInput) {
          // Igual que handleOpenFormPanel: las tasas de sensibilización se
          // re-derivan de resultados si el input persistido las trae vacías
          // (p. ej. cálculos creados antes de autocompletarlas).
          const savedResultados = Array.isArray((dataObj as any)?.resultados)
            ? (dataObj as any).resultados[0]
            : undefined;
          const toPct = (val: any): string | null => {
            if (val == null || val === "") return null;
            const num = Number(String(val).trim().replace("%", "").replace(",", "."));
            if (!Number.isFinite(num)) return null;
            const pct = Math.abs(num) < 1 && num !== 0
              ? Math.round(num * 10000) / 100
              : Math.round(num * 100) / 100;
            return String(pct);
          };
          setFormData((prev) => {
            const merged: Record<string, any> = { ...prev, ...latestInput };
            const fill = (key: string, val: any) => {
              if (!merged[key] && val != null && String(val).trim() !== "") {
                const pct = toPct(val);
                if (pct !== null) merged[key] = pct;
              }
            };
            fill("revenue_forecast_rate", savedResultados?.conceptos?.tasa_forecast);
            fill("fdc_forecast_rate", savedResultados?.integrado?.tasa_forecast);
            fill("perpetual_growth_rate", savedResultados?.conceptos?.tasa_perpetua ?? savedResultados?.integrado?.tasa_perpetua);
            fill("capex_income_rate", savedResultados?.capex_income_rate);
            fill("cto_income_rate", savedResultados?.cto_income_rate);
            return merged as typeof prev;
          });

          if (latestInput.balance_table) {
            setBalanceTable(latestInput.balance_table);
          }
          if (latestInput.results_table) {
            setResultsTable(latestInput.results_table);
          }

          if (setFileUploaded && (latestInput.fileUsername || latestInput.balance_table)) {
            setFileUploaded(true);
          }

          setHasCalculated(true);
          let sensibilizacionesData = extractSensibilizaciones(
            calculationData.data
          );
          // Patch Kapital-style: si entry no trae subsector, usa último input sensibilización
          const latestSubsector = (latestInput as any)?.subsector_sensibilizacion;
          const latestTickers = (latestInput as any)?.tickers_subsector_sensibilizacion;
          if ((latestSubsector || latestTickers) && sensibilizacionesData.length > 0) {
            sensibilizacionesData = sensibilizacionesData.map((entry) => ({
              ...entry,
              subsector: (entry as any).subsector || latestSubsector || undefined,
              tickers: (entry as any).tickers || latestTickers || undefined,
            }));
          }
          setSensibilizaciones(sensibilizacionesData);
          setSelectedSensIdx(sensibilizacionesData.length > 0 ? sensibilizacionesData.length - 1 : 0);
          const hasSavedSensitivity = sensibilizacionesData.length > 0;
          setResultView(hasSavedSensitivity ? "sensibilidad" : "original");
           ui.setShowResults(true);
           ui.setIsDesktopFormOpen(false);
           setIsSessionFresh(false);
           addToast("info", `Cálculo Valora (${code}) cargado correctamente.`);
        }
      }
    } catch (error) {
      console.error("Error loading Valora calculation from URL", error);
    }
  };

  return {
    currentCalculation,
    isLoading,
    isNativeLoading,
    hasCalculated,
    isSessionFresh,
    setIsSessionFresh,
    resultView,
    setResultView,
    sensibilizaciones,
    selectedSensIdx,
    setSelectedSensIdx,
    handleSubmit,
    handleNativeSubmit,
    loadFromUrl,
  };
}
